/**
 * ChatEmptyState — welcome screen rendered when no messages exist yet in the
 * DOCKED / slide-over geometry. Shows a hero greeting, the multilingual
 * "Languages" line, and the quick-start prompt chips.
 *
 * The full-width geometry has its own richer starting screen (IonHome.jsx).
 * Both now render the SAME language line and the SAME prompt list — see
 * IonLanguageLine.jsx and `ion.welcome.prompt.*` in i18n. The typewriter used
 * to live here as a private component; it was extracted so the landing screen
 * could show it without a second copy of the animation.
 *
 * Prompt chips are static for F1 (per design-lock §10 open question).
 * Phase D may swap them to adaptive chips driven by chatContextExtractor.
 */

import { chatStreaming, draftInput, insightsTokenBudget } from '../../state/chatStore.js'
import { sendChatMessage } from '../../hooks/useChatStream.js'
import { IonLockup } from './IonLockup.jsx'
import { IonLanguageLine } from './IonLanguageLine.jsx'
import { isDemoCatalogUnlocked } from '../../state/demoLimitStore.js'
import { t } from '../../i18n/index.js'

function defaultPrompts() {
  return [
    t('ion.welcome.prompt.emsi'),
    t('ion.welcome.prompt.ragone'),
    t('ion.welcome.prompt.ocv'),
    t('ion.welcome.prompt.impedance'),
  ]
}

/**
 * @param {object} props
 * @param {string} [props.userName] — first-name for the greeting
 * @param {string[]} [props.prompts] — override default prompt chips
 * @param {object} [props.contextHint] — passed through to sendChatMessage
 *
 * Note: callers may still pass a `context` prop; it is intentionally ignored
 * now that the CONTEXT card was removed from the Ion home (BA, 2026-07-23).
 */
export function ChatEmptyState({
  userName,
  prompts,
  contextHint = null,
}) {
  const resolvedPrompts = prompts ?? defaultPrompts()
  const streaming = chatStreaming.value

  function handleChipClick(prompt) {
    // Fix B (S4 fix-wave): a stream can in principle already be live even
    // while chatMessages is still empty (e.g. mid-hydration of a returning
    // conversation) — guard defensively so a chip click never queues a
    // SECOND stream alongside one already in flight. Mirrors ChatInput's
    // `canSend` gate (chatStore.js), which is the same chatStreaming check.
    if (streaming) return
    sendChatMessage(prompt, { contextHint })
  }

  function handleChipFill(prompt) {
    draftInput.value = prompt
  }

  const greeting = userName
    ? t('ion.welcome.hello.named', { name: userName })
    : t('ion.welcome.hello.anon')

  // DEMO onboarding hint: surface the daily Ion Insights Token budget once,
  // up front, on the welcome screen — but only when there IS a finite,
  // positive cap to report (WS 2.3; switched from the request-count budget
  // to the token budget 2026-07-17 when the request-count cap was removed).
  // `insightsTokenBudget` is populated by the same
  // `ionAccessService.bootstrapChatAccess()` call the persistent
  // `IonUsageMeter` reads (GET /chat/usage/me); `limit == null` means
  // unknown/unlimited and must render nothing here either.
  const demoTokenLimit = Number(insightsTokenBudget.value.limit)
  const showDemoBudget =
    isDemoCatalogUnlocked() &&
    Number.isFinite(demoTokenLimit) &&
    demoTokenLimit > 0

  return (
    <div class="ion-empty">
      <div class="ion-empty__hero">
        <IonLockup size={30} animated className="ion-empty__lockup" />
        <h2 class="ion-empty__title">{greeting}</h2>
        <IonLanguageLine />
        {showDemoBudget && (
          <p class="ion-empty__demo-budget">
            {t('ion.welcome.demo_budget', { limit: demoTokenLimit })}
          </p>
        )}
      </div>

      <ul class="ion-empty__prompts">
        {resolvedPrompts.map((prompt, idx) => (
          <li key={idx}>
            <button
              type="button"
              class="ion-empty__prompt"
              disabled={streaming}
              onClick={() => handleChipClick(prompt)}
              onMouseDown={(e) => {
                // Right-click / middle-click stages the prompt instead of sending.
                if (e.button === 1) {
                  e.preventDefault()
                  handleChipFill(prompt)
                }
              }}
            >
              {prompt}
            </button>
          </li>
        ))}
      </ul>

      <p class="ion-empty__hint">
        {/* {kbd} is left unsubstituted so we can interpolate the <kbd> JSX */}
        {t('ion.welcome.hint')
          .split('{kbd}')
          .reduce((acc, part, i, arr) => {
            acc.push(part)
            if (i < arr.length - 1) acc.push(<kbd key={i}>⌘K</kbd>)
            return acc
          }, [])}
      </p>
    </div>
  )
}
